import type { Deadline } from '@/modules/common/types/ui.ts';
import { DateTime } from 'luxon';
import type { ValidatorFn } from '@/modules/common/models/schema/schema-control.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';

export default class Validator {
  static required: ValidatorFn<unknown> = (value: unknown) => {
    const isValid = !ValueUtil.isEmpty(value, { zero: false, false: true });

    if (!isValid) {
      return 'ui.field.error.fillRequiredField';
    }
  };

  static notBlank: ValidatorFn<string> = (value: string) => {
    if (value && !value.trim()) {
      return 'ui.field.error.fillRequiredField';
    }
  };

  static min(min: number): ValidatorFn<number> {
    return (value: number) => {
      const isValid = value >= min;

      if (!isValid) {
        return { key: 'ui.field.error.valueMustBeGreaterOrEqual', params: { value: min } };
      }
    };
  }

  static max(max: number): ValidatorFn<number> {
    return (value: number) => {
      const isValid = value <= max;

      if (!isValid) {
        return { key: 'ui.field.error.valueMustBeLessOrEqual', params: { value: max } };
      }
    };
  }

  static time: ValidatorFn<string> = (value: string) => {
    const isValid = /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);

    if (!isValid) {
      return { key: 'ui.field.error.enterValidTime', params: { format: 'HH:MM' } };
    }
  };

  static deadlineMoreOrEqualThanToday: ValidatorFn<Deadline> = (deadline: Deadline) => {
    const isValid = deadline.date >= DateTime.now().startOf('day');

    if (!isValid) {
      return 'ui.field.error.dateMustBeGreaterOrEqualCurrent';
    }
  };

  static deadlineMoreThanToday: ValidatorFn<Deadline> = (deadline: Deadline) => {
    const isValid = deadline.date > DateTime.now().startOf('day');

    if (!isValid) {
      return 'ui.field.error.dateMustBeGreaterCurrent';
    }
  };

  static digitsLength(...lengths: number[]): ValidatorFn<string> {
    return (value: string) => {
      if (!value) {
        return;
      }

      if (!/^\d+$/.test(value) || !lengths.includes(value.length)) {
        return { key: 'ui.field.error.enterValidDigitsLength', params: { lengths: lengths.join(', ') } };
      }
    };
  }

  static digitsLengthBetween(min: number, max: number): ValidatorFn<string> {
    return (value: string) => {
      if (!value) {
        return;
      }

      if (!/^\d+$/.test(value) || value.length < min || value.length > max) {
        return { key: 'ui.field.error.enterValidDigitsLengthRange', params: { min, max } };
      }
    };
  }

  private static innChecksum(value: string): boolean {
    const digits = value.split('').map(Number);

    if (digits.length === 10) {
      const weights = [2, 4, 10, 3, 5, 9, 4, 6, 8];
      const checksum = (weights.reduce((sum, weight, index) => sum + weight * digits[index], 0) % 11) % 10;

      return checksum === digits[9];
    }

    if (digits.length === 12) {
      const weights1 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
      const weights2 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
      const n11 = (weights1.reduce((sum, weight, index) => sum + weight * digits[index], 0) % 11) % 10;
      const n12 = (weights2.reduce((sum, weight, index) => sum + weight * digits[index], 0) % 11) % 10;

      return n11 === digits[10] && n12 === digits[11];
    }

    return false;
  }

  private static ogrnChecksum(value: string): boolean {
    if (value.length === 13) {
      return Number((BigInt(value.slice(0, 12)) % 11n) % 10n) === Number(value[12]);
    }

    if (value.length === 15) {
      return Number((BigInt(value.slice(0, 14)) % 13n) % 10n) === Number(value[14]);
    }

    return false;
  }

  static inn: ValidatorFn<string> = (value: string) => {
    const lengthError = Validator.digitsLength(10, 12)(value);

    if (lengthError) {
      return lengthError;
    }

    if (value && !Validator.innChecksum(value)) {
      return 'ui.field.error.enterValidInn';
    }
  };

  static kpp = Validator.digitsLength(9);

  static ogrn: ValidatorFn<string> = (value: string) => {
    const lengthError = Validator.digitsLength(13, 15)(value);

    if (lengthError) {
      return lengthError;
    }

    if (value && !Validator.ogrnChecksum(value)) {
      return 'ui.field.error.enterValidOgrn';
    }
  };

  static okpo = Validator.digitsLength(8, 10);
  static okato = Validator.digitsLengthBetween(2, 11);
}
